import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import useEvents from './useEvents';
import useAttendance from './useAttendance';
import useReports from './useReports';
import useModals from './useModals';
import useUtils from './useUtils';
import useMeetSync from './useMeetSync';

const useDashboard = () => {
  const { user } = useAuth();
  const events = useEvents();
  const attendance = useAttendance();
  const reports = useReports();
  const modals = useModals();
  const utils = useUtils();
  const meetSync = useMeetSync();

  // Combined actions
  const handleEventSelect = useCallback(async (event) => {
    events.selectEvent(event);
    modals.openEventDetailModal();
    attendance.clearAttendanceData();
    meetSync.clearMeetData();
    
    // Cargar estado de Meet si el evento tiene ID de base de datos
    if (event.id) {
      const eventDbId = await events.getEventDbId(event.id, event);
      if (eventDbId) {
        await meetSync.loadMeetStatus(eventDbId);
      }
    }
  }, [events, modals, attendance, meetSync]);

  const handleShowAttendance = useCallback(async () => {
    modals.openAttendanceModal();
    events.clearEvents();
    await events.loadTodayEvents(user?.email);
  }, [modals, events, user]);



  const handleShowReports = useCallback(async () => {
    modals.openReportsModal();
    reports.clearReports();
    await reports.loadReportEvents(user?.email);
  }, [modals, reports, user]);

  const handleTabSelect = useCallback(async (tabKey) => {
    modals.changeTab(tabKey);
    
    if (tabKey === 'attendance') {
      // Solo limpiar si NO hay datos previos o si es un evento diferente
      if (!attendance.validationResult && !attendance.attendanceQR && !attendance.attendanceLink) {
        attendance.clearAttendanceData();
      }
      
      if (events.selectedEvent?.id) {
        const eventDbId = await events.getEventDbId(events.selectedEvent.id, events.selectedEvent);
        
        // Ejecutar ambas operaciones en paralelo para mejor rendimiento
        const promises = [];
        
        // Cargar estado de Meet
        if (eventDbId) {
          promises.push(meetSync.loadMeetStatus(eventDbId));
        }
        
        // Solo validar si NO hay datos ya cargados
        if (!attendance.validationResult) {
          promises.push(
            attendance.validateAndGenerateCredentials(
              events.selectedEvent.id,
              events.selectedEvent,
              user?.email
            )
          );
        }
        
        // Esperar a que ambas terminen
        await Promise.all(promises);
      }
    } else if (tabKey === 'reports') {
      reports.clearReports();
    }
  }, [modals, attendance, reports, events, user, meetSync]);

  const handleCopyLink = useCallback(async (url) => {
    return await utils.copyToClipboard(url);
  }, [utils]);

  const handleDownloadQR = useCallback((qrCodeImage, eventTitle) => {
    return utils.downloadQRImage(qrCodeImage, eventTitle);
  }, [utils]);

  const handleSyncMeet = useCallback(async () => {
    if (!events.selectedEvent?.id) return;
    meetSync.setSyncing(true);
    
    const eventDbId = await events.getEventDbId(events.selectedEvent.id, events.selectedEvent);
    if (eventDbId) {
      await meetSync.syncMeet(eventDbId, false);
    } else {
      meetSync.setSyncing(false);
    }
  }, [events, meetSync]);

  const handleResyncMeet = useCallback(async () => {
    if (!events.selectedEvent?.id) return;
    meetSync.setSyncing(true);
    
    const eventDbId = await events.getEventDbId(events.selectedEvent.id, events.selectedEvent);
    if (eventDbId) {
      await meetSync.syncMeet(eventDbId, true);
    } else {
      meetSync.setSyncing(false);
    }
  }, [events, meetSync]);



  const handleRefreshMeetStatus = useCallback(async () => {
    if (!events.selectedEvent?.id) return;
    
    const eventDbId = await events.getEventDbId(events.selectedEvent.id, events.selectedEvent);
    if (eventDbId) {
      await meetSync.loadMeetStatus(eventDbId);
    }
  }, [events, meetSync]);

  return {
    // User
    user,
    
    // All hook states and actions
    events,
    attendance,
    reports,
    modals,
    utils,
    meetSync,
    
    // Combined actions
    handleEventSelect,
    handleShowAttendance,
    handleShowReports,
    handleTabSelect,
    handleCopyLink,
    handleDownloadQR,
    handleSyncMeet,
    handleResyncMeet,
    handleRefreshMeetStatus
  };
};

export default useDashboard;